import type { PointIcon } from '@technology/web3d-plugin-icons';
import {
  LinearFilter,
  MeshPointsGeometry,
  Texture,
  type MeshPoints,
  type ThreejsPlugin,
} from '@technology/web3d-plugin-threejs';
import { getLogger } from '@trimble/logging';
import type { IssueMarkerUserData } from './issue-marker-sprite.types';

const spriteTextures = new WeakMap<MeshPoints, Texture>();

export type IssueMarkerImageLoader = (
  iconPath: string
) => Promise<ImageBitmap | undefined>;

/** Decodes each icon path once. A failed load is dropped so the next call retries it. */
export function createIssueMarkerImageLoader(): IssueMarkerImageLoader {
  const images = new Map<string, Promise<ImageBitmap | undefined>>();

  return (iconPath) => {
    const cached = images.get(iconPath);
    if (cached) {
      return cached;
    }

    const loading = decodeIconImage(iconPath).catch((error: unknown) => {
      images.delete(iconPath);
      // An empty texture keeps the sprite placed and pickable.
      getLogger().warn('Failed to load issue marker icon texture', {
        error,
        iconPath,
      });
      return undefined;
    });
    images.set(iconPath, loading);
    return loading;
  };
}

/** Bound to the viewer that owns `threejs`. Does not touch a model. */
export async function createIssueMarkerSprites(
  threejs: ThreejsPlugin | undefined,
  icons: readonly PointIcon[],
  loadImage: IssueMarkerImageLoader
): Promise<MeshPoints[]> {
  if (icons.length === 0) {
    return [];
  }

  if (!threejs) {
    getLogger().warn('ThreejsPlugin is not available for issue markers');
    return [];
  }

  const images = await Promise.all(
    icons.map((icon) => loadImage(icon.iconPath))
  );

  return icons.map((icon, index) => {
    const geometry = new MeshPointsGeometry();
    // instancePosition is float32 and jitters ~0.06 m at world scale.
    // Leave it at the origin and store the position on the float64 object transform.
    geometry.setPositions(new Float32Array([0, 0, 0]));

    // One texture per sprite, because disposeIssueMarkerSprites disposes each
    // sprite's map. Texture.dispose() leaves the shared image intact.
    const texture = createIconTexture(images[index]);
    const material = threejs.createMeshPointsMaterial({
      map: texture,
      size: icon.size,
      transparent: true,
      sizeAttenuation: false,
      usePointUv: false,
      depthTest: false,
    });
    const sprite = threejs.createMeshPoints(geometry, material);
    spriteTextures.set(sprite, texture);
    const userData: IssueMarkerUserData = { markerHashId: icon.id };
    sprite.userData = userData;
    sprite.position.set(icon.position.x, icon.position.y, icon.position.z);
    return sprite;
  });
}

export function disposeIssueMarkerSprites(
  sprites: readonly MeshPoints[]
): void {
  for (const sprite of sprites) {
    // clear(), not dispose(): instances share one module-level position buffer.
    sprite.geometry.clear();

    spriteTextures.get(sprite)?.dispose();
    spriteTextures.delete(sprite);

    const materials = Array.isArray(sprite.material)
      ? sprite.material
      : [sprite.material];
    for (const material of materials) {
      material.dispose();
    }
  }
}

async function decodeIconImage(iconPath: string): Promise<ImageBitmap> {
  const response = await fetch(iconPath);
  const blob = await response.blob();
  return createImageBitmap(blob, {
    imageOrientation: 'flipY',
    premultiplyAlpha: 'none',
  });
}

function createIconTexture(image: ImageBitmap | undefined): Texture {
  const texture = new Texture();
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.generateMipmaps = false;
  if (image) {
    texture.image = image;
  }
  texture.needsUpdate = true;
  return texture;
}
